import PropTypes from "prop-types";
import { useState } from "react";
import {
  Card,
  CardPreview,
  Text,
  ActionButton,
  ColorSwatchPicker,
  ColorSwatch,
  ColorField,
  DialogTrigger,
  Popover,
  LabeledValue,
  parseColor,
} from "@react-spectrum/s2";
import { Button as AriaButton } from "react-aria-components";
import SwitchIcon from "@react-spectrum/s2/icons/Switch";
import { HoverOverlay } from "@app/components/HoverOverlay";
import { ThumbnailCaption } from "@app/components/ThumbnailCaption";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

/**
 * A color template asset picked from the scraped `colors` (or typed in).
 *
 * Captioned like `ImageAsset`: the `description` follows the swatch. With
 * `isCaptioned` the label still heads the swatch, but the `contextualHelp`
 * moves below it, to the row of the description (see `ThumbnailCaption`),
 * spanning the container; the `children` come last.
 */
export const ColorAsset = ({
  label,
  description,
  contextualHelp,
  color,
  colors,
  name,
  onChange,
  isCaptioned = false,
  children,
}) => {
  const [isActive, setActive] = useState(false);

  const picker = (
    <DialogTrigger onOpenChange={setActive}>
      <AriaButton
        className={(state) =>
          style({
            boxSizing: "border-box",
            width: 72,
            padding: 4,
            borderWidth: 2,
            borderStyle: "solid",
            borderColor: {
              isHovered: "gray-600",
              isPressed: "gray-1000",
              isActive: "gray-1000",
              default: "gray-300",
            },
            borderRadius: "xl",
            position: "relative",
            backgroundColor: "transparent",
            overflow: "hidden",
            transition: "colors",
          })({ ...state, isActive })
        }
      >
        {({ isHovered }) => (
          <>
            <Card
              variant="quiet"
              styles={style({
                width: "auto",
                minWidth: 0,
                // margin: 2,
              })}
            >
              <CardPreview
                UNSAFE_className={style({
                  aspectRatio: "square",
                })}
              >
                <div
                  className={style({
                    aspectRatio: "square",
                    "--slash-color": {
                      type: "backgroundColor",
                      value:
                        "var(--lightningcss-light, #d73220) var(--lightningcss-dark, #fc432e)",
                    },
                  })}
                  style={{
                    background:
                      color ||
                      "linear-gradient(to bottom right, transparent calc(50% - 2px), var(--slash-color) calc(50% - 2px) calc(50% + 2px), transparent calc(50% + 2px)) no-repeat",
                  }}
                />
              </CardPreview>
            </Card>
            <HoverOverlay
              isHovered={isHovered}
              icon={SwitchIcon}
              radius="default"
            />
          </>
        )}
      </AriaButton>

      <Popover size="S" placement="end">
        <div
          className={style({
            display: "flex",
            flexDirection: "column",
            gap: 24,
            padding: 12,
          })}
        >
          <div>
            <Text styles={style({ font: "title" })}>
              Select {(label || name)?.toLowerCase()}
            </Text>
          </div>
          <ColorSwatchPicker
            value={!!color ? parseColor(color) : undefined}
            onChange={(color) => {
              if (color) onChange?.({ color, name });
            }}
          >
            {[...new Set([...(colors ?? []), color])].map((color) => (
              <ColorSwatch key={color} color={color} />
            ))}
          </ColorSwatchPicker>
          <ColorField
            label="Color"
            labelPosition="side"
            colorSpace="rgb"
            value={color || ""}
            onChange={(color) => onChange({ color, name })}
            prefix={
              <ColorSwatch
                size="XS"
                color={color ? color.toString("hex") : undefined}
              />
            }
          />
        </div>
      </Popover>
    </DialogTrigger>
  );

  // A caption below the 72px swatch spans the whole container.
  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        width: { default: 72, isCaptioned: "full" },
      })({ isCaptioned })}
    >
      {!!label && (
        // Only a heading: a `LabeledValue` cannot contain editable values.
        <LabeledValue
          label={label}
          value=""
          contextualHelp={isCaptioned ? undefined : contextualHelp}
        />
      )}
      {picker}
      <ThumbnailCaption
        description={description}
        contextualHelp={isCaptioned ? contextualHelp : undefined}
      />
      {!!children && <div className={style({ marginTop: 16 })}>{children}</div>}
    </div>
  );
};

ColorAsset.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  isCaptioned: PropTypes.bool,
  contextualHelp: PropTypes.node,
  color: PropTypes.string,
  colors: PropTypes.array,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};
